import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import "./HealthStatusBar.css";

/**
 * Sistem sağlık durumu üst bandı.
 * 5sn polling ile telegram + WS bağlantı durumlarını izler.
 * Donma (freeze) aktifse büyük uyarı banner'ı gösterir.
 */
export default function HealthStatusBar() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["health-probe"],
    queryFn: api.healthProbe,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });

  if (isLoading || !data) {
    return (
      <div className="hsb hsb--loading" data-testid="health-status-bar">
        <Dot status="loading" /> <span>Sağlık kontrolü…</span>
      </div>
    );
  }

  const freeze = data.freeze || {};
  if (freeze.active) {
    return <FreezeBanner freeze={freeze} qc={qc} />;
  }

  return (
    <div
      className={`hsb ${data.all_healthy ? "hsb--ok" : "hsb--warn"}`}
      data-testid="health-status-bar"
    >
      <ServiceStatus
        label="Telegram"
        status={data.telegram?.healthy ? "ok" : "down"}
        detail={data.telegram?.active ? "bot aktif" : "bot kapalı"}
        testId="health-telegram"
      />
      <ServiceStatus
        label="Binance"
        status={data.binance?.healthy ? "ok" : "down"}
        detail={formatAge(data.binance)}
        testId="health-binance"
      />
      <ServiceStatus
        label="Darkex"
        status={data.darkex?.healthy ? "ok" : "down"}
        detail={formatAge(data.darkex)}
        testId="health-darkex"
      />
      <div className="hsb-overall" data-testid="health-overall">
        {data.all_healthy ? (
          <>
            <Dot status="ok" /> Tüm sistemler aktif
          </>
        ) : (
          <>
            <Dot status="warn" /> {data.healthy_count}/{data.total_count} sağlam
          </>
        )}
      </div>
    </div>
  );
}

function ServiceStatus({ label, status, detail, testId }) {
  return (
    <div className={`hsb-svc hsb-svc--${status}`} data-testid={testId}>
      <Dot status={status} />
      <div className="hsb-svc-text">
        <div className="hsb-svc-label">{label}</div>
        <div className="hsb-svc-detail">{detail}</div>
      </div>
    </div>
  );
}

function Dot({ status }) {
  return <span className={`hsb-dot hsb-dot--${status}`} />;
}

function formatAge(svc) {
  if (!svc) return "—";
  if (!svc.connected) return svc.error ? "bağlantı yok" : "kopuk";
  const ageMs = svc.last_msg_age_ms;
  if (ageMs == null) return "—";
  if (ageMs < 1000) return `${ageMs}ms`;
  if (ageMs < 60_000) return `${(ageMs / 1000).toFixed(1)}s`;
  return `${Math.floor(ageMs / 60_000)}d`;
}

function FreezeBanner({ freeze, qc }) {
  const [showUnfreeze, setShowUnfreeze] = useState(false);
  const [reason, setReason] = useState("");

  const unfreeze = useMutation({
    mutationFn: () => api.setFreeze(false, reason || "Manuel açma"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["health-probe"] });
      setShowUnfreeze(false);
      setReason("");
    },
  });

  return (
    <div className="hsb hsb--freeze" data-testid="health-freeze-banner">
      <div className="hsb-freeze-icon" aria-hidden>🥶</div>
      <div className="hsb-freeze-body">
        <div className="hsb-freeze-title">İŞLEM DONDURULDU</div>
        <div className="hsb-freeze-reason" data-testid="health-freeze-reason">
          {freeze.reason || "Sebep belirtilmemiş"}
        </div>
        <div className="hsb-freeze-meta">
          Kaynak: <b>{freeze.source || "system"}</b>
          {freeze.at && ` · ${new Date(freeze.at).toLocaleString("tr-TR")}`}
        </div>
      </div>
      {!showUnfreeze ? (
        <button
          type="button"
          className="hsb-unfreeze-btn"
          onClick={() => setShowUnfreeze(true)}
          data-testid="health-unfreeze-btn"
        >
          Çöz ve Aç
        </button>
      ) : (
        <div className="hsb-unfreeze-form">
          <input
            type="text"
            placeholder="Açma sebebi (audit için zorunlu)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="health-unfreeze-reason-input"
          />
          <button
            type="button"
            disabled={!reason.trim() || unfreeze.isPending}
            onClick={() => unfreeze.mutate()}
            data-testid="health-unfreeze-confirm-btn"
          >
            {unfreeze.isPending ? "…" : "Aç"}
          </button>
          <button
            type="button"
            className="hsb-cancel"
            onClick={() => setShowUnfreeze(false)}
          >
            İptal
          </button>
        </div>
      )}
    </div>
  );
}
